import React, { useState, useEffect, useMemo } from 'react';
import { Search, Repeat, UserPlus, UserMinus, Crown, AlertCircle, ChevronRight, ChevronDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';

// Manager headshot (the app ships /{owner}main.png for each manager). Falls back
// to a colored initial when an image is missing (e.g. former managers like Sam).
const ManagerAvatar = ({ owner, size = 26, ring = 'ring-gray-200' }) => {
  const [broken, setBroken] = useState(false);
  if (!owner) return null;
  if (broken) {
    return (
      <div className={`rounded-full bg-purple-200 text-purple-800 font-bold flex items-center justify-center flex-shrink-0 ring-2 ${ring}`}
        style={{ width: size, height: size, fontSize: size * 0.45 }} title={owner}>
        {owner[0]}
      </div>
    );
  }
  return (
    <img src={`/${owner.toLowerCase()}main.png`} alt={owner} title={owner}
      onError={() => setBroken(true)}
      className={`rounded-full object-cover bg-gray-100 flex-shrink-0 ring-2 ${ring}`}
      style={{ width: size, height: size }} />
  );
};

// the manager most relevant to an event (the destination for trades)
const eventOwner = (e) => (e.kind === 'offseason' ? e.to : e.owner);

// Per-event-kind visual treatment.
const KIND = {
  season: { dot: 'bg-purple-500', icon: null },
  add: { dot: 'bg-emerald-500', icon: UserPlus },
  trade: { dot: 'bg-amber-500', icon: Repeat },
  offseason: { dot: 'bg-indigo-500', icon: Repeat },
  drop: { dot: 'bg-gray-400', icon: UserMinus },
  projection: { dot: 'bg-yellow-500', icon: Crown },
};

function describe(e) {
  switch (e.kind) {
    case 'season': return e.status === 'Keeper' ? `Kept by ${e.owner}` : `Drafted by ${e.owner}`;
    case 'add': return `Added by ${e.owner}`;
    case 'trade': return `Traded to ${e.owner}${e.from ? ` (from ${e.from})` : ''}`;
    case 'offseason': return `Offseason trade: ${e.from} → ${e.to}`;
    case 'drop': return `Dropped by ${e.owner}`;
    case 'projection': return `2026 keeper option — ${e.owner}`;
    default: return e.owner;
  }
}

// Salary-chart dot color = how the player was on the roster that season.
const ACQ = {
  drafted: { color: '#10b981', label: 'Drafted' }, // emerald
  kept: { color: '#7c3aed', label: 'Kept' },        // purple
  projected: { color: '#eab308', label: 'Projected' }, // amber (2026)
};
const acqOf = (d) => (d.kind === 'projection' ? ACQ.projected : /keep/i.test(d.status || '') ? ACQ.kept : ACQ.drafted);

// recharts clones this per point, passing the point payload — so each dot is colored individually.
const SalaryDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={4} fill={acqOf(payload).color} stroke="#fff" strokeWidth={1.5} />;
};

const SalaryTip = ({ active, payload, marks }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const detail = marks?.[d.year]?.detail || [];
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow px-3 py-2 text-xs">
      <div className="font-semibold text-gray-800">{d.year} season</div>
      <div className="text-gray-600">${d.cost} · {acqOf(d).label}</div>
      {detail.map((t, i) => (
        <div key={i} className="text-gray-500 mt-0.5 flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: t.color }} />{t.text}
        </div>
      ))}
    </div>
  );
};

// Contract badge for a season/projection row. History contracts are inferred by the
// build (e.contract / e.standardKeep); the 2026 projection carries the engine's status.
const DEAL = {
  contract: 'text-violet-700 bg-violet-100',
  expired: 'text-orange-700 bg-orange-100',
  standard: 'text-gray-500 bg-gray-100',
};
const dealYearLabel = (year) => (year >= 3 ? '3-yr deal · final' : `3-yr deal · yr ${year}`);
function dealBadge(e) {
  if (e.kind === 'projection') {
    switch (e.status) {
      case 'year2': return { text: dealYearLabel(2), cls: DEAL.contract };
      case 'final': return { text: dealYearLabel(3), cls: DEAL.contract };
      case 'expired': return { text: 'contract expired +$20', cls: DEAL.expired };
      case 'standard': return { text: 'standard +$5', cls: DEAL.standard };
      default: return null;
    }
  }
  if (e.kind !== 'season') return null;
  if (e.contract?.expired) return { text: 'contract expired +$20', cls: DEAL.expired };
  if (e.contract?.year) return { text: dealYearLabel(e.contract.year), cls: DEAL.contract };
  if (e.standardKeep) return { text: 'standard +$5', cls: DEAL.standard };
  return null;
}

const TimelineEvent = ({ e, last }) => {
  const meta = KIND[e.kind] || KIND.season;
  const Icon = meta.icon;
  const deal = dealBadge(e);
  return (
    <div className="flex gap-3">
      {/* rail */}
      <div className="flex flex-col items-center">
        <div className={`h-3 w-3 rounded-full ${meta.dot} ring-4 ring-white shadow z-10 mt-1.5`} />
        {!last && <div className="w-0.5 flex-1 bg-gray-200" />}
      </div>
      {/* content */}
      <div className="pb-5 flex-1 min-w-0">
        {(e.date || e.kind === 'projection' || e.kind === 'offseason') && (
          <div className="flex items-baseline gap-2 flex-wrap">
            {e.date && <span className="text-xs text-gray-400">{e.date}</span>}
            {e.kind === 'projection' && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-yellow-700 bg-yellow-100 rounded px-1.5 py-0.5">projected</span>
            )}
            {e.kind === 'offseason' && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700 bg-indigo-100 rounded px-1.5 py-0.5">offseason</span>
            )}
          </div>
        )}
        <div className="text-gray-700 flex items-center gap-2">
          <ManagerAvatar owner={eventOwner(e)} size={24} />
          {Icon && <Icon className="h-3.5 w-3.5 text-gray-400" />}
          <span>{describe(e)}</span>
        </div>
        <div className="flex gap-2 mt-1 flex-wrap text-xs">
          {e.cost != null && (
            <span className="font-semibold text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">
              ${e.cost}{e.kind === 'add' ? ' FAAB' : ''}
            </span>
          )}
          {deal && (
            <span className={`rounded px-1.5 py-0.5 ${deal.cls}`} title={e.keepMath || (e.contract?.signedYear ? `signed ${e.contract.signedYear}, $${e.contract.flatCost} flat` : undefined)}>
              {deal.text}
            </span>
          )}
          {e.via && e.kind === 'add' && (
            <span className="text-gray-500 bg-gray-50 rounded px-1.5 py-0.5">{e.via}</span>
          )}
          {e.cutCost != null && (
            <span className="text-rose-600 bg-rose-50 rounded px-1.5 py-0.5">cut ${e.cutCost}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Collapse a set of years into compact stints: [2016,2017,2020,2022] → "2016–2017, 2020, 2022".
// Captures isolated years and gaps instead of a misleading first–last range.
function formatYearRanges(years) {
  const ys = [...new Set(years)].sort((a, b) => a - b);
  const parts = [];
  let start = null, prev = null;
  for (const y of ys) {
    if (start === null) { start = prev = y; continue; }
    if (y === prev + 1) { prev = y; continue; }
    parts.push(start === prev ? `${start}` : `${start}–${prev}`);
    start = prev = y;
  }
  if (start !== null) parts.push(start === prev ? `${start}` : `${start}–${prev}`);
  return parts.join(', ');
}

// Career summary tiles (counts derived from the event log).
const STAT_TILES = [
  { key: 'drafted', label: 'Drafted', color: '#10b981' },
  { key: 'kept', label: 'Kept', color: '#7c3aed' },
  { key: 'added', label: 'Added', color: '#0ea5e9' },
  { key: 'dropped', label: 'Dropped', color: '#64748b' },
  { key: 'trades', label: 'Trades', color: '#d97706' },
  { key: 'contracts', label: 'Contracts', color: '#4f46e5' },
];

// The individual events behind a stat tile, for the expand/collapse detail list.
function statDetails(key, events) {
  const rows = [];
  const isKeep = (e) => /keep/i.test(e.status || '');
  events.forEach((e) => {
    if (key === 'drafted' && e.kind === 'season' && !isKeep(e)) {
      rows.push({ year: e.year, owner: e.owner, note: `$${e.cost}` });
    } else if (key === 'kept' && e.kind === 'season' && isKeep(e)) {
      const c = e.contract;
      const tag = c?.expired ? 'expired +$20'
        : c?.year ? (c.year >= 3 ? '3-yr deal · final' : `3-yr deal · yr ${c.year}`)
        : 'standard +$5';
      rows.push({ year: e.year, owner: e.owner, note: `$${e.cost} · ${tag}` });
    } else if (key === 'added' && e.kind === 'add') {
      rows.push({ year: e.year, date: e.date, owner: e.owner, note: `$${e.cost} FAAB${e.via ? ` · ${e.via}` : ''}` });
    } else if (key === 'dropped' && e.kind === 'drop') {
      rows.push({ year: e.year, date: e.date, owner: e.owner, note: 'dropped' });
    } else if (key === 'trades' && e.kind === 'trade') {
      rows.push({ year: e.year, date: e.date, owner: e.owner, note: `acquired${e.from ? ` from ${e.from}` : ''}` });
    } else if (key === 'trades' && e.kind === 'offseason') {
      rows.push({ year: e.year, owner: e.to, note: `offseason · from ${e.from}` });
    } else if (key === 'contracts' && e.kind === 'season' && e.contract?.year === 1) {
      rows.push({ year: e.year, owner: e.owner, note: `$${e.contract.flatCost} flat · 3 yrs` });
    }
  });
  return rows;
}

const PlayerHistory = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [expandedStat, setExpandedStat] = useState(null);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ''}/player-history.json`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  const matches = useMemo(() => {
    if (!data || query.trim().length < 2) return [];
    const q = query.toLowerCase();
    return data.players.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 15);
  }, [data, query]);

  // a few notable quick-picks (most timeline events = most league tenure/activity)
  const featured = useMemo(() => {
    if (!data) return [];
    return [...data.players].sort((a, b) => b.events.length - a.events.length).slice(0, 8);
  }, [data]);

  // salary trajectory: one cost point per season (season anchors + 2026 projection),
  // tagged with how the player was rostered that year so each dot can be colored.
  const costData = useMemo(() => {
    if (!selected) return [];
    const byYear = {};
    selected.events.forEach((e) => {
      if ((e.kind === 'season' || e.kind === 'projection') && e.cost != null) {
        byYear[e.year] = { cost: e.cost, status: e.status, kind: e.kind };
      }
    });
    return Object.keys(byYear).map(Number).sort((a, b) => a - b)
      .map((y) => ({ year: y, ...byYear[y] }));
  }, [selected]);

  // Who holds the player now. A 2026 projection means they're a current keeper option
  // (still rostered); anything else as the last event means they're no longer owned.
  const holder = useMemo(() => {
    if (!selected) return null;
    const last = selected.events[selected.events.length - 1];
    return { owner: eventOwner(last), current: last.kind === 'projection' };
  }, [selected]);

  // group consecutive events by season so the timeline can shade alternating bands
  const seasonGroups = useMemo(() => {
    if (!selected) return [];
    const groups = [];
    selected.events.forEach((e) => {
      const g = groups[groups.length - 1];
      if (g && g.year === e.year) g.events.push(e);
      else groups.push({ year: e.year, events: [e] });
    });
    return groups;
  }, [selected]);

  // chart annotations: ownership changes (trades / offseason trades) + contract milestones
  // (3-yr deal signed, contract expired), keyed to seasons that actually appear on the chart.
  const chartMarks = useMemo(() => {
    if (!selected) return {};
    const years = new Set(costData.map((d) => d.year));
    const marks = {};
    const ensure = (y) => (marks[y] = marks[y] || { detail: [], label: null, color: null });
    const setLabel = (m, label, color, override) => {
      if (override || !m.label) { m.label = label; m.color = color; }
    };
    selected.events.forEach((e) => {
      if (!years.has(e.year)) return;
      if (e.kind === 'trade') {
        const m = ensure(e.year);
        m.detail.push({ text: `Traded to ${e.owner}${e.from ? ` (from ${e.from})` : ''}`, color: '#d97706' });
        setLabel(m, `→ ${e.owner}`, '#d97706', true);
      } else if (e.kind === 'offseason') {
        const m = ensure(e.year);
        m.detail.push({ text: `Offseason trade: ${e.from} → ${e.to}`, color: '#4f46e5' });
        setLabel(m, `→ ${e.to}`, '#4f46e5', true);
      } else if (e.kind === 'season' && e.contract?.year === 1) {
        const m = ensure(e.year);
        m.detail.push({ text: `3-yr deal signed ($${e.contract.flatCost} flat)`, color: '#7c3aed' });
        setLabel(m, 'deal', '#7c3aed', false);
      } else if (e.kind === 'season' && e.contract?.expired) {
        const m = ensure(e.year);
        m.detail.push({ text: 'Contract expired (+$20)', color: '#ea580c' });
        setLabel(m, '+$20', '#ea580c', false);
      }
    });
    return marks;
  }, [selected, costData]);

  // per-manager ownership summary: seasons rostered, salary committed, FAAB spent, tenure span.
  // Ranked so the manager who owned the player the most sits on top.
  const byManager = useMemo(() => {
    if (!selected) return [];
    const m = {};
    const ensure = (o) => (m[o] = m[o] || { owner: o, salary: 0, faab: 0, faabAdds: 0, seasonYears: [], eventYears: new Set() });
    selected.events.forEach((e) => {
      if (e.kind === 'season' && e.owner) {
        const r = ensure(e.owner); r.seasonYears.push(e.year); r.salary += e.cost || 0; r.eventYears.add(e.year);
      } else if (e.kind === 'add' && e.owner) {
        const r = ensure(e.owner); r.faab += e.cost || 0; r.faabAdds += 1; r.eventYears.add(e.year);
      } else if (e.kind === 'trade' && e.owner) {
        ensure(e.owner).eventYears.add(e.year);
      } else if (e.kind === 'offseason' && e.to) {
        ensure(e.to).eventYears.add(e.year);
      }
      // drops don't add tenure; the 2026 projection is excluded (not realized ownership)
    });
    return Object.values(m)
      .map((r) => ({
        owner: r.owner, seasons: r.seasonYears.length, salary: r.salary, faab: r.faab, faabAdds: r.faabAdds,
        tenure: formatYearRanges(r.seasonYears), // actual stints, e.g. "2016–2017, 2020, 2022"
        pickupYear: r.eventYears.size ? Math.min(...r.eventYears) : null,
      }))
      .sort((a, b) => b.seasons - a.seasons || b.salary - a.salary || b.faab - a.faab);
  }, [selected]);

  // career activity counts: drafted / kept / added / dropped / traded / contracts signed
  const careerStats = useMemo(() => {
    if (!selected) return null;
    const s = { drafted: 0, kept: 0, added: 0, dropped: 0, trades: 0, contracts: 0 };
    selected.events.forEach((e) => {
      if (e.kind === 'season') {
        if (/keep/i.test(e.status || '')) s.kept += 1; else s.drafted += 1;
        if (e.contract?.year === 1) s.contracts += 1; // a 3-yr deal was signed this season
      } else if (e.kind === 'add') s.added += 1;
      else if (e.kind === 'drop') s.dropped += 1;
      else if (e.kind === 'trade' || e.kind === 'offseason') s.trades += 1; // in-season + offseason
    });
    return s;
  }, [selected]);

  if (error) {
    return (
      <div className="max-w-xl mx-auto mt-12 p-6 rounded-xl bg-red-50 border border-red-200 text-red-800 flex items-start gap-3">
        <AlertCircle className="h-6 w-6 flex-shrink-0" />
        <div>
          <p className="font-bold">Couldn’t load player history.</p>
          <p className="text-sm mt-1">{error}</p>
          <p className="text-sm mt-2 text-red-600">Run <code className="bg-red-100 px-1 rounded">node scripts/build/buildPlayerHistory.js</code>.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-purple-700">
        <div className="animate-spin h-8 w-8 border-4 border-purple-300 border-t-purple-700 rounded-full mr-3" />
        Loading history…
      </div>
    );
  }

  const pick = (p) => { setSelected(p); setQuery(''); setExpandedStat(null); };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-gray-900">Player History</h1>
        <p className="text-gray-500 mt-1">
          Every player’s full story — salaries, owners, trades, and moves from 2012 to today
          ({data.players.length.toLocaleString()} players).
        </p>
      </div>

      {/* search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a player…"
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none"
        />
        {matches.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-100 max-h-72 overflow-y-auto">
            {matches.map((p) => (
              <button key={p.key} onClick={() => pick(p)}
                className="w-full text-left px-4 py-2 hover:bg-purple-50 flex items-center justify-between">
                <span className="font-medium text-gray-800">{p.name}</span>
                <span className="text-xs text-gray-400">{p.events.length} events <ChevronRight className="inline h-3 w-3" /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* featured quick-picks when nothing selected */}
      {!selected && (
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-2">Most league history</p>
          <div className="flex flex-wrap gap-2">
            {featured.map((p) => (
              <button key={p.key} onClick={() => pick(p)}
                className="px-3 py-1.5 rounded-lg text-sm bg-white border border-gray-200 hover:bg-purple-50 text-gray-700">
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* timeline */}
      {selected && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 px-6 py-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <ManagerAvatar owner={holder.owner} size={44} ring="ring-white/40" />
              <div className="min-w-0">
                <h2 className="text-2xl font-bold truncate">{selected.name}</h2>
                <span className="text-purple-200 text-sm">
                  {holder.current ? 'current' : 'last owned by'}: {holder.owner}
                </span>
              </div>
            </div>
            <span className="text-purple-200 text-sm flex-shrink-0">
              {selected.events[0]?.year}–{selected.events[selected.events.length - 1]?.year}
            </span>
          </div>

          {/* career summary — tap a tile to expand the events behind it */}
          {careerStats && (
            <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {STAT_TILES.map((t) => {
                  const n = careerStats[t.key];
                  const active = expandedStat === t.key;
                  const clickable = n > 0;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      disabled={!clickable}
                      onClick={() => setExpandedStat(active ? null : t.key)}
                      className={`text-center rounded-lg py-2 transition relative ${
                        active ? 'bg-gray-100 ring-1 ring-gray-300'
                        : clickable ? 'bg-gray-50 hover:bg-gray-100 cursor-pointer'
                        : 'bg-gray-50 opacity-60 cursor-default'}`}
                    >
                      <div className="text-xl font-extrabold tabular-nums" style={{ color: t.color }}>{n}</div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5 flex items-center justify-center gap-0.5">
                        {t.label}
                        {clickable && <ChevronDown className={`h-3 w-3 transition-transform ${active ? 'rotate-180' : ''}`} />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* expanded detail list for the selected stat */}
              {expandedStat && (() => {
                const tile = STAT_TILES.find((t) => t.key === expandedStat);
                const rows = statDetails(expandedStat, selected.events);
                return (
                  <div className="mt-2 rounded-lg border border-gray-100 overflow-hidden">
                    <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide bg-gray-50 border-b border-gray-100"
                      style={{ color: tile.color }}>
                      {tile.label} · {rows.length}
                    </div>
                    <div className="divide-y divide-gray-50">
                      {rows.map((r, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <span className="text-xs font-bold text-gray-400 tabular-nums w-9 flex-shrink-0">{r.year}</span>
                          <ManagerAvatar owner={r.owner} size={20} />
                          <span className="text-gray-700">{r.owner}</span>
                          {r.date && <span className="text-xs text-gray-400">{r.date}</span>}
                          <span className="ml-auto text-gray-500 text-xs text-right">{r.note}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* salary over time */}
          {costData.length >= 2 && (
            <div className="px-4 sm:px-6 pt-5 border-b border-gray-100 pb-4">
              <div className="flex items-center justify-between flex-wrap gap-y-1 mb-2">
                <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Salary over time</p>
                <div className="flex items-center gap-3 text-[11px] text-gray-500">
                  {Object.values(ACQ).map((a) => (
                    <span key={a.label} className="flex items-center gap-1">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: a.color }} />{a.label}
                    </span>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={185}>
                <LineChart data={costData} margin={{ top: 20, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" width={44} tickFormatter={(v) => `$${v}`} />
                  <Tooltip content={(props) => <SalaryTip {...props} marks={chartMarks} />} />
                  {Object.entries(chartMarks).map(([year, m]) => (
                    <ReferenceLine
                      key={year}
                      x={Number(year)}
                      stroke={m.color}
                      strokeDasharray="3 3"
                      strokeOpacity={0.55}
                      label={{ value: m.label, position: 'top', fontSize: 9, fill: m.color }}
                    />
                  ))}
                  <Line type="monotone" dataKey="cost" stroke="#cbd5e1" strokeWidth={2.5} dot={<SalaryDot />} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* by manager — who owned this player, and how much they invested */}
          {byManager.length > 0 && (
            <div className="px-4 sm:px-6 pt-5 border-b border-gray-100 pb-4">
              <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-3">By manager</p>
              <div className="space-y-1">
                {byManager.map((r, idx) => {
                  const maxSeasons = byManager[0].seasons || 1;
                  const barPct = r.seasons ? Math.round((r.seasons / maxSeasons) * 100) : 0;
                  return (
                    <div key={r.owner} className="relative rounded-lg overflow-hidden">
                      {/* tenure bar — width ∝ seasons owned */}
                      <div className="absolute inset-y-0 left-0 bg-purple-100/70" style={{ width: `${barPct}%` }} />
                      <div className="relative flex items-center gap-3 px-2 py-1.5">
                        <ManagerAvatar owner={r.owner} size={30} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-800 text-sm">{r.owner}</span>
                            {idx === 0 && r.seasons > 0 && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-700 bg-purple-100 rounded px-1.5 py-0.5">most owned</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {r.seasons > 0
                              ? `${r.seasons} season${r.seasons !== 1 ? 's' : ''}${r.tenure ? ` · ${r.tenure}` : ''}`
                              : `pickup only${r.pickupYear ? ` · ${r.pickupYear}` : ''}`}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {r.salary > 0 && <div className="text-sm font-semibold text-emerald-700">${r.salary}</div>}
                          {r.faab > 0 && <div className="text-xs text-gray-400">${r.faab} FAAB</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="p-6">
            {seasonGroups.map((g, gi) => (
              <div key={`${g.year}-${gi}`} className={`-mx-3 px-3 pt-2 pb-1 rounded-xl ${gi % 2 === 1 ? 'bg-gray-50' : ''}`}>
                {/* year header */}
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-base font-extrabold text-gray-800 tabular-nums">{g.year}</span>
                  <span className="h-px flex-1 bg-gray-200/70" />
                </div>
                {/* that season's events, indented beneath the year */}
                <div className="pl-4 sm:pl-6">
                  {g.events.map((e, j) => (
                    <TimelineEvent
                      key={`${gi}-${j}`}
                      e={e}
                      last={j === g.events.length - 1}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerHistory;
